import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import cloneDeep from 'lodash/cloneDeep';
import { BehaviorSubject, Observable, ReplaySubject } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { State } from '../../reducers';
import { resetUser, setUser, setUserSettings, setUserSubstitutees, setUserTheme } from '../../reducers/user/user.actions';
import { API } from '../constants/api.constants';
import { SystemUserModel, UserModel, UserTheme } from '../models/user.model';
import { PitMsalService } from './pit-msal.service';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  /**
   * Compares target user against system user (logged in user) that may substitute other users.
   *
   * @returns boolean
   */
  static userApplicable(systemUser: SystemUserModel, targetUser: UserModel | string) {
    const targetUserLogin = typeof targetUser === 'string' ? targetUser : targetUser?.userName || null;
    const systemUserLogins: string [] = [systemUser.userName, ...(systemUser.substitutedUsers || [])];

    return systemUserLogins.includes(targetUserLogin);
  }
  private nameSubject = new BehaviorSubject<string>(null);
  private loggedInProduceit = new BehaviorSubject<boolean>(false);
  private userDetails: SystemUserModel;
  private userDetailsSubject = new ReplaySubject<SystemUserModel>(1);

  constructor(private http: HttpClient, private store: Store<State>, private pitMsalService: PitMsalService) {}

  getCurrentUserName() {
    return window.localStorage.getItem('fusion-produceit-user');
  }

  public setCurrentUserName(newUserName: string): void {
    window.localStorage.setItem('fusion-produceit-user', newUserName);
    this.nameSubject.next(newUserName);
    this.store.dispatch(setUser({user: newUserName}));
  }

  usernameChange() {
    return this.nameSubject.asObservable();
  }

  authAccount$(): Observable<unknown> {
    return this.pitMsalService.getAccount$();
  }

  login(): Observable<any> {
    return this.authAccount$().pipe(switchMap(() => this.http.get(API.authorities, {})));
  }

  public resetUser(): void {
    window.localStorage.removeItem('fusion-produceit-user');
    window.localStorage.removeItem('REQUEST_LIST_FILTERS');
    this.store.dispatch(resetUser({user: null}));
  }

  setLoggedInProduceit(fullyLoaded: boolean): void {
    this.loggedInProduceit.next(fullyLoaded);
  }

  isLoggedInProduceit(): boolean {
    return this.loggedInProduceit.value;
  }

  getLoggedInProduceit() {
    return this.loggedInProduceit.asObservable();
  }

  updateUserDetails() {
    this.http.get(API.userDetails, {}).subscribe((userDetails: SystemUserModel) => {
      this.userDetails = userDetails;
      this.userDetailsSubject.next(userDetails);
      this.store.dispatch(setUserSubstitutees({substitutees: userDetails?.substitutedUsers || []}));
      this.store.dispatch(setUserTheme({theme: userDetails?.settings.displayMode || UserTheme.LIGHT}));
      this.store.dispatch(setUserSettings({settings: userDetails?.settings}));
    });
  }

  getUserDetails() {
    return cloneDeep(this.userDetails);
  }

  getUserDetails$() {
    return this.userDetailsSubject;
  }

  isMimicMode(authUserEmail: string): boolean {
    const realUserName = authUserEmail ? authUserEmail.substring(0, authUserEmail.indexOf('@')) : '';
    const mimicUserName = this.getCurrentUserName();
    return realUserName.toLocaleLowerCase() !== mimicUserName?.toLocaleLowerCase();
  }
}
